import { TailSpin } from 'react-loader-spinner';
import { ImageSelectorMedia } from '@/services/editorService';
import { TaskStatus } from '@/services/uploaderService';
import alertIcon from '@/public/images/icons/alert.svg';
import binIcon from '@/public/images/icons/bin.svg';
import redoIcon from '@/public/images/icons/redo.svg';
import checkIcon from '@/public/images/icons/check--white.svg';
import { useLocalization } from '@/services/localizationService';

export function ImageCard({
  id,
  shouldLightUp,
  media,
  selected,
  isCurrent,
  showDeleteButton,
  onChangeShownMedia,
  onReprocessFailedMedia,
  onDeleteMedia,
  onToggleMedia,
}: {
  id: string;
  shouldLightUp: boolean;
  media: ImageSelectorMedia;
  selected: boolean;
  isCurrent: boolean;
  showDeleteButton: boolean;
  onChangeShownMedia: (event: ImageSelectorMedia) => void;
  onReprocessFailedMedia: (mediaToReprocess: ImageSelectorMedia) => void;
  onDeleteMedia: (mediaToDelete: ImageSelectorMedia) => void;
  onToggleMedia: ({
    toggledMedia,
    checked,
  }: {
    toggledMedia: ImageSelectorMedia;
    checked: boolean;
  }) => void;
}) {
  const { t, data } = useLocalization();
  return (
    <div
      id={id}
      className={`group img-gradient-overlay rounded-lg overflow-hidden cursor-pointer mb-2 ${
        selected || isCurrent
          ? 'border-2 border-white'
          : 'border-2 border-transparent'
      } ${
        id && shouldLightUp ? 'z-30 pointer-events-none' : 'pointer-events-auto'
      }`}
      key={media.id}
    >
      <div
        onClick={() => {
          if (
            media.status !== TaskStatus.Failed &&
            media.status !== TaskStatus.Processing
          ) {
            onChangeShownMedia(media);
          }
        }}
        className={`absolute w-[100%] h-[100%] group-hover:visible flex items-center justify-center ${
          media.status === TaskStatus.Processing ||
          media.status === TaskStatus.Failed
            ? 'visible'
            : 'invisible'
        }`}
      >
        <div className="w-[100%] h-[100%] bg-black opacity-60" />
        {media.status === TaskStatus.Processing && (
          <div className="absolute">
            <TailSpin
              color="white"
              height="20"
              width="20"
              aria-label="loading"
            />
          </div>
        )}
      </div>
      {media.status === TaskStatus.Failed && (
        <div className="absolute w-[100%] h-[100%] flex flex-col justify-center items-center">
          <img {...alertIcon} alt={`failed${media.id}`} />
          <button
            type="button"
            onClick={() => onReprocessFailedMedia(media)}
            className="absolute backdrop-blur-[2px] bottom-3 btn btn--secondary cursor-pointer text-[8px] uppercase font-bold bg-overlay-white-20 border-0 px-[7px] py-[2px]"
          >
            <img {...redoIcon} alt={`failed${media.id}`} />
            {t(data.common.retry)}
          </button>
        </div>
      )}
      {showDeleteButton && (
        <div
          onClick={() => onDeleteMedia(media)}
          className="bg-primary-accent w-[28px] h-[28px] rounded-full flex justify-center items-center absolute right-2 top-2 invisible group-hover:visible"
        >
          <img {...binIcon} alt={`delete${media.id}`} />
        </div>
      )}

      {media.status !== TaskStatus.Failed && (
        <>
          <input
            className="sr-only"
            type="checkbox"
            id={`select-${media.id}`}
            checked={selected}
            onChange={(e) => {
              if (isCurrent) {
                return;
              }

              onToggleMedia({
                toggledMedia: media,
                checked: e.target.checked,
              });
            }}
          />
          <label
            className={`absolute cursor-pointer box-content p-2 ${
              selected ? 'opacity-100' : 'opacity-50'
            }  hover:scale-110`}
            htmlFor={`select-${media.id}`}
          >
            <img className="w-[18px] h-[18px]" {...checkIcon} alt="check" />
          </label>
        </>
      )}

      <img
        className={media.status === TaskStatus.Failed ? 'min-h-[132px]' : ''}
        src={media.outputUrl ? media.outputUrl : media.inputUrl}
        alt={media.id}
      />
    </div>
  );
}
